
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { validateSession } from "@/lib/security/auth-validation";
import { toast } from "sonner";

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [validatingSession, setValidatingSession] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);

  useEffect(() => {
    // Skip validation if still loading initially
    if (loading) return;
    
    async function validateUserSession() {
      // Skip validation if no user is found after initial loading
      if (!user) {
        setValidatingSession(false);
        return;
      }

      try {
        const isValid = await validateSession();
        setSessionValid(isValid);
        
        if (!isValid) {
          console.error("Session validation failed");
        }
      } catch (error) {
        console.error("Session validation error:", error);
        setSessionValid(false);
      } finally {
        setValidatingSession(false);
      }
    }

    validateUserSession();
  }, [user, loading]);

  if (loading || validatingSession) {
    // Show loading state while checking authentication
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center">
          <div className="h-8 w-8 border-4 border-primary/50 border-t-primary rounded-full animate-spin mb-4"></div>
          <p className="text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!user || !sessionValid) {
    // Show a toast only if session is invalid but user exists in state
    if (user && !sessionValid) {
      toast.error('Session expired', {
        description: 'Please sign in again to continue',
      });
    }
    
    // Save the current path to sessionStorage for redirect after auth
    sessionStorage.setItem('redirectAfterAuth', location.pathname + location.search);
    
    // Redirect to the login page
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
